import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChecklistProvider } from './contexts/ChecklistContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Bevoelkerung from './pages/Bevoelkerung';
import Activate from './pages/Activate';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Imprint from './pages/Imprint';
import Privacy from './pages/Privacy';
import BrowserInfo from './pages/BrowserInfo';
import HackStory from './pages/HackStory';
import Profile from './pages/Profile';
import VerifyEmailChange from './pages/VerifyEmailChange';
import CancelEmailChange from './pages/CancelEmailChange';
import Admin from './pages/Admin';
import DigitaleSelbstverteidigung from './pages/DigitaleSelbstverteidigung';
import Quiz from './pages/Quiz';
import QuizResults from './pages/QuizResults';
import ChecklistDashboard from './pages/ChecklistDashboard';
import Checklist from './pages/Checklist';
import ModuleGuideGenerator from './pages/ModuleGuideGenerator';
import AdminRoute from './components/AdminRoute';
import BuzzwordChatbot from './components/BuzzwordChatbot';
import LogoutNotification from './components/LogoutNotification';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';

// Authenticated BuzzwordChatbot component
const AuthenticatedBuzzwordChatbot = () => {
  const { user, loading } = useAuth();
  
  // Only show the chatbot if user is authenticated and not loading
  if (loading || !user) {
    return null;
  }
  
  return <BuzzwordChatbot />;
};

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      'Futura Book',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});



// Main App Route component - shows Bevoelkerung after login
const MainAppRoute = () => {
  const { user, loading, getToken } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  // Check if user has a token but user state is not set yet
  const token = getToken();
  if (!user && !token) {
    return <Navigate to="/login" replace />;
  }
  
  // If user has a token but user state is not loaded, show loading
  if (!user && token) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Validating authentication...</div>
      </div>
    );
  }

  // After login, show only the Bevoelkerung page
  return <Bevoelkerung />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ChecklistProvider>
          <Router>
          {/* <DebugAuth /> */}
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/activate/:token" element={<Activate />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email-change/:token" element={<VerifyEmailChange />} />
            <Route path="/cancel-email-change/:token" element={<CancelEmailChange />} />
            {/* TEMPORARY: HackStory public - remove PrivateRoute wrapper */}
            <Route path="/hack-story/*" element={<HackStory />} />
            
            {/* Protected routes - require authentication */}
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/" element={<PrivateRoute><MainAppRoute /></PrivateRoute>} />
            <Route path="/dashboard" element={<PrivateRoute><MainAppRoute /></PrivateRoute>} />
            <Route path="/cyber-lagebild" element={<PrivateRoute><MainAppRoute /></PrivateRoute>} />
            <Route path="/bevoelkerung" element={<PrivateRoute><MainAppRoute /></PrivateRoute>} />
            <Route path="/browser-info" element={<PrivateRoute><BrowserInfo /></PrivateRoute>} />
            {/* TEMPORARY: HackStory moved to public routes above - restore this line to make it private again:
            <Route path="/hack-story/*" element={<PrivateRoute><HackStory /></PrivateRoute>} />
            */}
            <Route path="/digitale-selbstverteidigung" element={<PrivateRoute><DigitaleSelbstverteidigung /></PrivateRoute>} />
            <Route path="/quiz" element={<PrivateRoute><Quiz /></PrivateRoute>} />
            <Route path="/quiz-results" element={<PrivateRoute><QuizResults /></PrivateRoute>} />
            <Route path="/checklist" element={<PrivateRoute><ChecklistDashboard /></PrivateRoute>} />
            <Route path="/checklist/:category" element={<PrivateRoute><Checklist /></PrivateRoute>} />
            <Route path="/module-guide" element={<PrivateRoute><ModuleGuideGenerator /></PrivateRoute>} />
            <Route path="/module-guide/:moduleName" element={<PrivateRoute><ModuleGuideGenerator /></PrivateRoute>} />
            <Route path="/admin" element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            } />
            
            {/* Catch all other routes and redirect to main app */}
            <Route path="*" element={<MainAppRoute />} />
          </Routes>
          <AuthenticatedBuzzwordChatbot />
          <LogoutNotification />
        </Router>
        </ChecklistProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 